import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from '../../services/reducers/user';
import { Icon } from '@iconify/react';
import { API_URL } from '../../config/api';
import './WebhooksPage.css';

const WebhooksPage = () => {
  const user = useSelector(selectUser);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [userProducts, setUserProducts] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    events: [],
    products: [],
    is_active: true,
    description: ''
  });

  useEffect(() => {
    loadWebhooks();
    loadAvailableEvents();
    loadUserProducts();
  }, []);

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      console.log('🔍 Carregando webhooks...', { token: token ? 'presente' : 'ausente', API_URL });
      
      const response = await fetch(`${API_URL}/api/webhooks`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Resposta webhooks:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Webhooks carregados:', data);
        setWebhooks(data.data || []);
      } else {
        const errorData = await response.json();
        console.error('❌ Erro ao carregar webhooks:', errorData);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar webhooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableEvents = async () => {
    try {
      // Eventos hardcoded para garantir que sempre apareçam
      const defaultEvents = [
        'sale.created',
        'sale.updated', 
        'sale.paid',
        'sale.cancelled',
        'affiliate.approved',
        'affiliate.rejected',
        'withdrawal.requested',
        'withdrawal.approved',
        'withdrawal.rejected',
        'product.created',
        'product.updated',
        'product.deleted',
        'user.registered',
        'user.updated'
      ];
      
      setAvailableEvents(defaultEvents);
      
      // Tentar carregar do backend também (opcional)
      const token = localStorage.getItem('auth_token');
      
      try {
        const response = await fetch(`${API_URL}/api/webhooks/events`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data && data.data.length > 0) {
            setAvailableEvents(data.data);
          }
        }
      } catch (backendError) {
        console.log('Usando eventos padrão (backend não disponível)');
      }
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
      // Fallback para eventos padrão
      setAvailableEvents([
        'sale.created',
        'sale.updated', 
        'sale.paid',
        'sale.cancelled',
        'affiliate.approved',
        'affiliate.rejected',
        'withdrawal.requested',
        'withdrawal.approved',
        'withdrawal.rejected',
        'product.created',
        'product.updated',
        'product.deleted',
        'user.registered',
        'user.updated'
      ]);
    }
  };

  const loadUserProducts = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${API_URL}/api/products`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserProducts(data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  const handleCreateWebhook = () => {
    setEditingWebhook(null);
    setFormData({
      name: '',
      url: '',
      events: [],
      products: [],
      is_active: true,
      description: ''
    });
    setShowModal(true);
  };

  const handleEditWebhook = (webhook) => {
    setEditingWebhook(webhook);
    setFormData({
      name: webhook.name,
      url: webhook.url,
      events: typeof webhook.events === 'string' ? JSON.parse(webhook.events || '[]') : webhook.events || [],
      products: typeof webhook.products === 'string' ? JSON.parse(webhook.products || '[]') : webhook.products || [],
      is_active: webhook.is_active,
      description: webhook.description || ''
    });
    setShowModal(true);
  };

  const handleSaveWebhook = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      const url = editingWebhook 
        ? `${API_URL}/api/webhooks/${editingWebhook.id}`
        : `${API_URL}/api/webhooks`;
      
      const method = editingWebhook ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowModal(false);
        loadWebhooks();
      } else {
        const error = await response.json();
        alert(`Erro: ${error.message}`);
      }
    } catch (error) {
      console.error('Erro ao salvar webhook:', error);
      alert('Erro ao salvar webhook');
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Tem certeza que deseja deletar este webhook?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${API_URL}/api/webhooks/${webhookId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        loadWebhooks();
      } else {
        const error = await response.json();
        alert(`Erro: ${error.message}`);
      }
    } catch (error) {
      console.error('Erro ao deletar webhook:', error);
      alert('Erro ao deletar webhook');
    }
  };

  const handleTestWebhook = async (webhookId) => {
    try {
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${API_URL}/api/webhooks/${webhookId}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Teste de webhook enviado com sucesso!');
      } else {
        const error = await response.json();
        alert(`Erro: ${error.message}`);
      }
    } catch (error) {
      console.error('Erro ao testar webhook:', error);
      alert('Erro ao testar webhook');
    }
  };

  const handleEventToggle = (event) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }));
  };

  const handleProductToggle = (productId) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.includes(productId)
        ? prev.products.filter(p => p !== productId)
        : [...prev.products, productId]
    }));
  };

  const getEventDisplayName = (event) => {
    const eventNames = {
      'sale.created': 'Venda Criada',
      'sale.updated': 'Venda Atualizada',
      'sale.paid': 'Venda Paga',
      'sale.cancelled': 'Venda Cancelada',
      'affiliate.created': 'Afiliado Criado',
      'affiliate.approved': 'Afiliado Aprovado',
      'affiliate.rejected': 'Afiliado Rejeitado',
      'withdrawal.requested': 'Saque Solicitado',
      'withdrawal.approved': 'Saque Aprovado',
      'withdrawal.rejected': 'Saque Rejeitado',
      'product.created': 'Produto Criado',
      'product.updated': 'Produto Atualizado'
    };
    return eventNames[event] || event;
  };

  if (loading) {
    return (
      <div className="webhooks-page">
        <div className="loading-container">
          <Icon icon="eos-icons:loading" className="loading-icon" />
          <span>Carregando webhooks...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="webhooks-page">
      <div className="webhooks-header">
        <div className="header-content">
          <div className="header-title">
            <Icon icon="mdi:webhook" className="header-icon" />
            <h1>Webhooks</h1>
          </div>
          <p className="header-description">
            Configure webhooks para receber notificações em tempo real sobre eventos da sua plataforma
          </p>
        </div>
        <button 
          className="btn-create-webhook"
          onClick={handleCreateWebhook}
        >
          <Icon icon="mdi:plus" />
          Novo Webhook
        </button>
      </div>

      <div className="webhooks-content">
        {webhooks.length === 0 ? (
          <div className="empty-state">
            <Icon icon="mdi:webhook" className="empty-icon" />
            <h3>Nenhum webhook configurado</h3>
            <p>Configure seu primeiro webhook para receber notificações em tempo real</p>
            <button 
              className="btn-create-first"
              onClick={handleCreateWebhook}
            >
              <Icon icon="mdi:plus" />
              Criar Primeiro Webhook
            </button>
          </div>
        ) : (
          <div className="webhooks-grid">
            {webhooks.map((webhook) => (
              <div key={webhook.id} className="webhook-card">
                <div className="webhook-header">
                  <div className="webhook-title">
                    <h3>{webhook.name}</h3>
                    <span className={`status-badge ${webhook.is_active ? 'active' : 'inactive'}`}>
                      {webhook.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  <div className="webhook-actions">
                    <button 
                      className="btn-test"
                      onClick={() => handleTestWebhook(webhook.id)}
                      title="Testar webhook"
                    >
                      <Icon icon="mdi:play" />
                    </button>
                    <button 
                      className="btn-edit"
                      onClick={() => handleEditWebhook(webhook)}
                      title="Editar webhook"
                    >
                      <Icon icon="mdi:pencil" />
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDeleteWebhook(webhook.id)}
                      title="Deletar webhook"
                    >
                      <Icon icon="mdi:delete" />
                    </button>
                  </div>
                </div>

                <div className="webhook-content">
                  <div className="webhook-url">
                    <Icon icon="mdi:link" />
                    <span>{webhook.url}</span>
                  </div>
                  
                  {webhook.description && (
                    <div className="webhook-description">
                      <p>{webhook.description}</p>
                    </div>
                  )}

                  <div className="webhook-events">
                    <h4>Eventos configurados:</h4>
                    <div className="events-list">
                      {(typeof webhook.events === 'string' ? JSON.parse(webhook.events || '[]') : webhook.events || []).map((event, index) => (
                        <span key={index} className="event-tag">
                          {getEventDisplayName(event)}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="webhook-stats">
                    <div className="stat">
                      <Icon icon="mdi:send" />
                      <span>{webhook.delivery_count || 0} envios</span>
                    </div>
                    <div className="stat">
                      <Icon icon="mdi:check-circle" />
                      <span>{webhook.success_count || 0} sucessos</span>
                    </div>
                    <div className="stat">
                      <Icon icon="mdi:clock" />
                      <span>{new Date(webhook.created_at).toLocaleDateString('pt-BR')}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Criação/Edição - Estilo Tags/Afterpay */}
      {showModal && (
        <div className="webhook-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="webhook-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="webhook-modal-header">
              <div className="webhook-modal-title">
                <Icon icon="mdi:webhook" className="webhook-title-icon" />
                {editingWebhook ? 'Editar Webhook' : 'Novo Webhook'}
              </div>
              <button 
                className="webhook-modal-close"
                onClick={() => setShowModal(false)}
              >
                <Icon icon="mdi:close" />
              </button>
            </div>

            <div className="webhook-modal-body">
              <div className="webhook-modal-grid">
                {/* Coluna Esquerda - Dados do Webhook */}
                <div className="webhook-form-column">
                  <div className="webhook-form-section">
                    <span
                      className="webhook-section-title d-flex align-items-center" 
                      // style={{ display: 'block' }}
                    >
                      <Icon icon="mdi:cog" />
                      Configurações
                    </span>
                    
                    <div className="webhook-form-group">
                      <label>Nome do Webhook</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Ex: Notificações de Vendas"
                        className="webhook-input"
                      />
                    </div>

                    <div className="webhook-form-group">
                      <label>URL de Destino</label>
                      <input
                        type="url"
                        value={formData.url}
                        onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                        placeholder="https://seu-site.com/webhook"
                        className="webhook-input"
                      />
                    </div>

                    <div className="webhook-form-group">
                      <label>Descrição (opcional)</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Descreva o propósito deste webhook..."
                        rows="3"
                        className="webhook-textarea"
                      />
                    </div>

                    <div className="webhook-form-group">
                      <label className="webhook-switch-label">
                        <span>Webhook ativo</span>
                        <div className="webhook-switch-container">
                          <input
                            type="checkbox"
                            id="webhook-active"
                            checked={formData.is_active}
                            onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                            className="webhook-switch-input"
                          />
                          <label htmlFor="webhook-active" className="webhook-switch-slider">
                            <span className="webhook-switch-button"></span>
                          </label>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="webhook-form-section">
                    <span
                      className="webhook-section-title d-flex align-items-center" 
                      // style={{ display: 'block' }}
                    >
                      <Icon icon="mdi:lightning-bolt" />
                      Eventos ({formData.events.length})
                    </span>
                    
                    <div className="webhook-events-list">
                      {availableEvents.length === 0 ? (
                        <div className="webhook-empty-state">
                          <Icon icon="mdi:lightning-bolt-outline" className="webhook-empty-icon" />
                          <p>Carregando eventos...</p>
                        </div>
                      ) : (
                        availableEvents.map((event) => (
                          <button
                            key={event}
                            type="button"
                            className={`webhook-event-item ${formData.events.includes(event) ? 'selected' : ''}`}
                            onClick={() => handleEventToggle(event)}
                          >
                            <span className="webhook-event-dot"></span>
                            <span className="webhook-event-name">{getEventDisplayName(event)}</span>
                            <Icon 
                              icon={formData.events.includes(event) ? 'mdi:check-circle' : 'mdi:plus-circle-outline'} 
                              className="webhook-event-action"
                            />
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Coluna Direita - Produtos (como Tags) */}
                <div className="webhook-products-column">
                  <div className="webhook-products-section">
                    <span
                     className="webhook-section-title d-flex align-items-center"
                    //  style={{ display: 'block' }}
                     >
                      <Icon icon="mdi:package-variant" />
                      Produtos ({formData.products.length})
                    </span>
                    
                    <div className="webhook-products-list">
                      {userProducts.length === 0 ? (
                        <div className="webhook-empty-state">
                          <Icon icon="mdi:package-variant-outline" className="webhook-empty-icon" />
                          <p>Nenhum produto encontrado</p>
                        </div>
                      ) : (
                        userProducts.map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            className={`webhook-product-item ${formData.products.includes(product.id) ? 'selected' : ''}`}
                            onClick={() => handleProductToggle(product.id)}
                          >
                            <span className="webhook-product-dot"></span>
                            <span className="webhook-product-name">{product.name}</span>
                            <Icon 
                              icon={formData.products.includes(product.id) ? 'mdi:check-circle' : 'mdi:plus-circle-outline'} 
                              className="webhook-product-action"
                            />
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="webhook-modal-footer">
              <button 
                className="webhook-btn-cancel"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </button>
              <button 
                className="webhook-btn-save"
                onClick={handleSaveWebhook}
              >
                <Icon icon="mdi:content-save" />
                {editingWebhook ? 'Atualizar' : 'Criar'} Webhook
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhooksPage;
